import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, ApiError, type FinishAttemptResponse } from '../lib/api';
import { maybeAskForReview } from '../lib/review';
import { useFetch } from '../lib/useFetch';
import { Loader } from '../components/Loader';
import { ErrorView } from '../components/ErrorView';
import { PageHeader } from '../components/PageHeader';
import { formatDuration } from '../lib/format';
import { IconTile } from '../components/AppIcons';

export function Result() {
  const { attemptId } = useParams<{ attemptId: string }>();
  const [data, setData] = useState<FinishAttemptResponse | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const me = useFetch(() => api.me(), []);

  useEffect(() => {
    if (!attemptId) return;
    // finishAttempt ідемпотентний — після повторного виклику просто повертає попередній результат
    api
      .finishAttempt(attemptId)
      .then((r) => {
        setData(r);
        // Момент радості — тут доречно попросити оцінку в Play (лише нативна апка,
        // лише після зданого, з власним кулдауном; далі вирішує сам Google).
        void maybeAskForReview(r.passed);
      })
      .catch((e) => setError(e instanceof ApiError ? e : new Error(String(e))));
  }, [attemptId]);

  if (error) return <ErrorView error={error} />;
  if (!data) return <Loader label="Підрахунок результату…" />;

  const percentage = data.total ? Math.round((data.score / data.total) * 100) : 0;

  return (
    <>
      <PageHeader title="Результат" />
      <div className="space-y-4 p-4">
        <section
          className={`card flex flex-col items-center gap-2 py-8 ${
            data.passed ? 'border border-tg-success' : 'border border-tg-destructive'
          }`}
        >
          <div className="text-5xl">{data.passed ? '🎉' : '😔'}</div>
          <div className="text-3xl font-bold">
            {data.score} / {data.total}
          </div>
          <div className="text-sm text-tg-hint">{percentage}% правильних</div>
          <div className={`text-sm font-medium ${data.passed ? 'text-tg-success' : 'text-tg-destructive'}`}>
            {data.passed ? 'Тест здано!' : 'Тест не здано'}
          </div>
        </section>

        <section className="grid grid-cols-2 gap-2 text-center">
          <div className="card">
            <div className="text-xl font-semibold">+{data.xpEarned}</div>
            <div className="text-xs text-tg-hint">XP отримано</div>
          </div>
          <div className="card">
            <div className="text-xl font-semibold">{formatDuration(data.timeSpentMs)}</div>
            <div className="text-xs text-tg-hint">Час</div>
          </div>
        </section>

        {data.newAchievements.length > 0 && (
          <section className="card space-y-2 border border-tg-accent">
            <h3 className="flex items-center gap-2 font-semibold">
              <IconTile kind="medal" className="h-6 w-6 rounded-md" glyphClass="h-3.5 w-3.5" />
              Нові досягнення
            </h3>
            <ul className="space-y-1 text-sm text-tg-hint">
              {data.newAchievements.map((slug) => (
                <li key={slug}>• {slug}</li>
              ))}
            </ul>
          </section>
        )}

        {!data.passed && me.data && !me.data.user.isPremium && (
          <section className="card space-y-2 border border-yellow-500 bg-yellow-500/5">
            <div className="flex items-start gap-2.5">
              <IconTile kind="crown" className="h-9 w-9 rounded-lg" glyphClass="h-5 w-5" />
              <div>
                <div className="font-semibold">Не здав? З Premium вистачить однієї спроби.</div>
                <div className="mt-1 text-xs text-tg-hint">
                  Безкоштовно ти бачиш лише 40% бази питань. Premium відкриває повний доступ —
                  усі режими, всі питання, мої помилки і закладки.
                </div>
              </div>
            </div>
            <Link to="/premium" className="btn-primary block w-full bg-tg-success text-center text-white">
              Відкрити Premium
            </Link>
          </section>
        )}

        <div className="space-y-2 pt-2">
          <Link to="/categories" className="btn-primary block w-full text-center">
            Ще один тест
          </Link>
          <Link to="/" className="btn-secondary block w-full text-center">
            На головну
          </Link>
        </div>
      </div>
    </>
  );
}
